import { useState } from 'react';
import { Copy, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { OtpCodeInput } from '../OtpCodeInput';
import { apiService, type Enable2FAResponse } from '../../services/api';
import { copyToClipboard } from '../../utils/clipboard';
import { extractErrorMessage } from '../../utils/errorHandler';
import { isCompleteOtp, mapEnableError } from '../../utils/twoFactor';

interface TwoFactorSetupPanelProps {
  /** Wird aufgerufen, nachdem das Backend den Bestätigungscode akzeptiert hat (2FA ist dann aktiv). */
  onActivated: () => void;
}

/**
 * Einrichtungs-Ablauf für 2FA: Setup starten, QR-Code/Secret anzeigen und mit einem
 * 6-stelligen Code bestätigen. 2FA ist erst nach erfolgreicher Bestätigung aktiv.
 */
export function TwoFactorSetupPanel({ onActivated }: TwoFactorSetupPanelProps) {
  const [setup, setSetup] = useState<Enable2FAResponse | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [code, setCode] = useState('');
  const [isEnabling, setIsEnabling] = useState(false);
  const [error, setError] = useState('');

  const handleStart = async () => {
    setIsStarting(true);
    setError('');
    try {
      setSetup(await apiService.setup2FA());
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err, '2FA-Einrichtung konnte nicht gestartet werden.'));
    } finally {
      setIsStarting(false);
    }
  };

  const handleEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEnabling || !isCompleteOtp(code)) return;
    setIsEnabling(true);
    setError('');
    try {
      await apiService.enable2FA(code);
      toast.success('Zwei-Faktor-Authentifizierung aktiviert.');
      onActivated();
    } catch (err: unknown) {
      setError(mapEnableError(err));
      setCode('');
    } finally {
      setIsEnabling(false);
    }
  };

  if (!setup) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Die Zwei-Faktor-Authentifizierung ist für dein Konto nicht aktiv. Schütze dein Konto, indem du einen
          zusätzlichen Code aus einer Authenticator-App (z. B. Google Authenticator oder Authy) verlangst.
        </p>
        <Button onClick={() => void handleStart()} disabled={isStarting} className="w-full">
          {isStarting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
          Einrichtung starten
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleEnable} className="space-y-5">
      <section className="space-y-3 text-center">
        <h4 className="font-medium">1. QR-Code scannen</h4>
        <p className="text-sm text-gray-600">Scanne den Code mit deiner Authenticator-App.</p>
        <div className="flex justify-center">
          <img src={setup.qrCode} alt="QR-Code für die Authenticator-App" className="border rounded-lg w-44 h-44" />
        </div>
        <div className="text-xs text-gray-500">
          <p>Oder gib diesen Schlüssel manuell ein:</p>
          <div className="flex items-center justify-center gap-2 mt-2">
            <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono break-all">{setup.secret}</code>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Schlüssel kopieren"
              onClick={() => void copyToClipboard(setup.secret, 'Schlüssel')}
            >
              <Copy className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h4 className="font-medium text-center">2. Code bestätigen</h4>
        <Label htmlFor="two-factor-setup-code" className="justify-center text-sm text-gray-600 font-normal">
          Gib den 6-stelligen Code aus deiner Authenticator-App ein.
        </Label>
        <OtpCodeInput
          id="two-factor-setup-code"
          value={code}
          onChange={setCode}
          disabled={isEnabling}
          invalid={!!error}
        />
        {error && (
          <Alert className="border-red-200 bg-red-50">
            <AlertDescription className="text-red-800">{error}</AlertDescription>
          </Alert>
        )}
      </section>

      <Button type="submit" className="w-full" disabled={isEnabling || !isCompleteOtp(code)}>
        {isEnabling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Aktivieren
      </Button>
    </form>
  );
}
